/* ==========================================================================
   Mondō London — the cursor dot
   A solid dot replaces the native cursor on pointer devices. Dual colour,
   never a blend filter: ink by default, flipping to paper over dark ground.
   Photographs are sampled — a tiny cached thumbnail of each image is read
   at the cursor position, so the dot flips by the local luminance of the
   very pixels beneath it. It trails the pointer with a lerped ease, swells
   over interactive elements, swells further over the gallery loupe,
   contracts while pressing, and yields to the native caret over text
   fields. Without JS the native cursor remains (html.has-dot gates all
   cursor:none rules).
   ========================================================================== */
(function () {
  'use strict';
  if (!(window.matchMedia && window.matchMedia('(pointer: fine)').matches)) return;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var dot = document.createElement('div');
  dot.className = 'cursor-dot is-off';
  dot.setAttribute('aria-hidden', 'true');
  var core = document.createElement('div');
  core.className = 'cursor-core';
  dot.appendChild(core);
  document.body.appendChild(dot);
  document.documentElement.classList.add('has-dot');

  /* ── movement: lerped pursuit ─────────────────────────────────────────── */
  var EASE = reduce ? 1 : 0.22;
  var tx = 0, ty = 0, cx = 0, cy = 0, raf = null, seen = false;
  function tick() {
    cx += (tx - cx) * EASE;
    cy += (ty - cy) * EASE;
    dot.style.transform = 'translate3d(' + cx + 'px,' + cy + 'px,0)';
    if (Math.abs(tx - cx) > 0.1 || Math.abs(ty - cy) > 0.1) raf = requestAnimationFrame(tick);
    else raf = null;
  }

  /* ── luminance sampling: tiny cached thumbnails of hovered images ─────── */
  var samples = {}; // src -> { ctx, w, h } | 'loading' | 'blocked'
  function thumbFor(img) {
    var src = img.currentSrc || img.src;
    if (!src) return null;
    var entry = samples[src];
    if (entry) return entry === 'loading' || entry === 'blocked' ? null : entry;
    samples[src] = 'loading';
    var probe = new Image();
    probe.crossOrigin = 'anonymous';
    try {
      var u = new URL(src, location.href);
      u.searchParams.set('width', '32');
      probe.src = u.toString();
    } catch (e) { probe.src = src; }
    probe.onload = function () {
      try {
        var w = 32, h = Math.max(2, Math.round(w * probe.naturalHeight / probe.naturalWidth));
        var cv = document.createElement('canvas');
        cv.width = w; cv.height = h;
        var ctx = cv.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(probe, 0, 0, w, h);
        ctx.getImageData(0, 0, 1, 1); // taint check
        samples[src] = { ctx: ctx, w: w, h: h };
      } catch (e) { samples[src] = 'blocked'; }
    };
    probe.onerror = function () { samples[src] = 'blocked'; };
    return null;
  }
  function luminanceAt(img, clientX, clientY) {
    var t = thumbFor(img);
    if (!t) return null;
    var r = img.getBoundingClientRect();
    if (!r.width || !r.height) return null;
    var rx = (clientX - r.left) / r.width, ry = (clientY - r.top) / r.height;
    // object-fit: cover mapping (default 50% position)
    var nw = img.naturalWidth || t.w, nh = img.naturalHeight || t.h;
    var scale = Math.max(r.width / nw, r.height / nh);
    var vw = r.width / scale, vh = r.height / scale;
    var fx = ((nw - vw) / 2 + rx * vw) / nw;
    var fy = ((nh - vh) / 2 + ry * vh) / nh;
    var px = Math.min(t.w - 2, Math.max(1, Math.round(fx * t.w)));
    var py = Math.min(t.h - 2, Math.max(1, Math.round(fy * t.h)));
    try {
      var d = t.ctx.getImageData(px - 1, py - 1, 3, 3).data;
      var lum = 0;
      for (var i = 0; i < d.length; i += 4) lum += 0.2126 * d[i] + 0.7152 * d[i + 1] + 0.0722 * d[i + 2];
      return lum / (d.length / 4) / 255;
    } catch (e) { return null; }
  }

  /* ── colour: the page inverts through the blend; photographs switch the
     dot to solid dual colour, flipped by the sampled local luminance ────── */
  var overImg = null;
  function setLight(on) { dot.classList.toggle('is-light', !!on); }
  function readGround(clientX, clientY) {
    dot.classList.toggle('is-photo', !!overImg);
    if (overImg) {
      var lum = luminanceAt(overImg, clientX, clientY);
      if (lum !== null) { setLight(lum < 0.5); return; }
      // unsampleable image: assume photographic (dark-leaning) once loaded
      if (samples[overImg.currentSrc || overImg.src] === 'blocked') { setLight(true); return; }
    }
    setLight(false);
  }

  document.addEventListener('mousemove', function (e) {
    tx = e.clientX; ty = e.clientY;
    if (!seen) { seen = true; cx = tx; cy = ty; dot.classList.remove('is-off'); }
    if (raf === null) raf = requestAnimationFrame(tick);
    readGround(e.clientX, e.clientY);
  }, { passive: true });

  document.documentElement.addEventListener('mouseleave', function () { dot.classList.add('is-off'); });
  document.documentElement.addEventListener('mouseenter', function () { if (seen) dot.classList.remove('is-off'); });

  /* ── states follow what sits beneath the pointer ──────────────────────── */
  document.addEventListener('mouseover', function (e) {
    var t = e.target;
    if (!t.closest) return;
    var galleryMain = t.closest('.gallery-main');
    var galleryControl = t.closest('[data-gallery-zoom],[data-gallery-prev],[data-gallery-next]');
    overImg = galleryControl ? null : (t.tagName === 'IMG' ? t : (t.closest('.ph, .gallery-main') ? t.closest('.ph, .gallery-main').querySelector('img') : null));
    readGround(e.clientX, e.clientY);
    var zoom = galleryMain && galleryMain.classList.contains('is-zoom') && !galleryControl;
    var field = t.closest('input:not([type="checkbox"]), textarea, select');
    var link = t.closest('a, button, summary, label, [role="button"], .linklike');
    dot.classList.toggle('is-zoom', !!zoom);
    dot.classList.toggle('is-link', !!link && !zoom);
    dot.classList.toggle('is-field', !!field);
  });
  document.addEventListener('mousedown', function () { dot.classList.add('is-press'); });
  document.addEventListener('mouseup', function () { dot.classList.remove('is-press'); });
})();
